import React from 'react';

const LIKELIHOOD_LABELS = ['rare', 'unlikely', 'possible', 'likely', 'almost_certain'];
const IMPACT_LABELS = ['negligible', 'minor', 'moderate', 'major', 'severe'];

const LIKELIHOOD_DISPLAY = {
  rare: 'Rare',
  unlikely: 'Unlikely',
  possible: 'Possible',
  likely: 'Likely',
  almost_certain: 'Almost Certain',
};

const IMPACT_DISPLAY = {
  negligible: 'Negligible',
  minor: 'Minor',
  moderate: 'Moderate',
  major: 'Major',
  severe: 'Severe',
};

const CATEGORY_COLORS = {
  infrastructure: '#6366F1',
  access_control: '#EC4899',
  data_protection: '#14B8A6',
  operational: '#F59E0B',
  vendor: '#8B5CF6',
  personnel: '#EF4444',
  compliance: '#3B82F6',
  application_security: '#10B981',
};

const getCellColor = (likelihoodIdx, impactIdx) => {
  const score = (likelihoodIdx + 1) * (impactIdx + 1);
  if (score <= 3) return 'bg-green-100';
  if (score <= 6) return 'bg-yellow-100';
  if (score <= 12) return 'bg-orange-100';
  return 'bg-red-100';
};

const RiskMatrix = ({ risks = [] }) => {
  // Build a map of (likelihood, impact) -> list of risks
  const riskMap = {};
  risks.forEach((risk) => {
    const key = `${risk.likelihood}_${risk.impact}`;
    if (!riskMap[key]) riskMap[key] = [];
    riskMap[key].push(risk);
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Risk Matrix</h3>
      <div className="flex">
        {/* Y-axis label */}
        <div className="flex flex-col justify-center mr-2">
          <span className="text-xs text-gray-500 transform -rotate-90 whitespace-nowrap">
            Likelihood
          </span>
        </div>

        <div className="flex-1">
          {/* Grid rows (top = almost_certain, bottom = rare) */}
          <div className="grid grid-cols-6 gap-px">
            {/* Header row */}
            <div className="w-20" />
            {IMPACT_LABELS.map((impact) => (
              <div
                key={impact}
                className="text-center text-xs text-gray-500 pb-1 truncate px-1"
                title={IMPACT_DISPLAY[impact]}
              >
                {IMPACT_DISPLAY[impact]}
              </div>
            ))}

            {/* Data rows (reversed so highest likelihood is on top) */}
            {[...LIKELIHOOD_LABELS].reverse().map((likelihood, liRevIdx) => {
              const likelihoodIdx = LIKELIHOOD_LABELS.length - 1 - liRevIdx;
              return (
                <React.Fragment key={likelihood}>
                  <div className="text-xs text-gray-500 text-right pr-2 flex items-center justify-end w-20 truncate" title={LIKELIHOOD_DISPLAY[likelihood]}>
                    {LIKELIHOOD_DISPLAY[likelihood]}
                  </div>
                  {IMPACT_LABELS.map((impact, impactIdx) => {
                    const key = `${likelihood}_${impact}`;
                    const cellRisks = riskMap[key] || [];
                    return (
                      <div
                        key={key}
                        className={`${getCellColor(likelihoodIdx, impactIdx)} border border-gray-200 rounded h-10 flex items-center justify-center gap-0.5 flex-wrap p-0.5`}
                        title={cellRisks.length > 0 ? cellRisks.map(r => r.title).join(', ') : undefined}
                      >
                        {cellRisks.map((risk) => (
                          <div
                            key={risk.id}
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: CATEGORY_COLORS[risk.category] || '#6B7280' }}
                            title={`${risk.title} (${risk.category})`}
                          />
                        ))}
                        {cellRisks.length === 0 && (
                          <span className="text-xs text-gray-300">
                            {(likelihoodIdx + 1) * (impactIdx + 1)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>

          {/* X-axis label */}
          <div className="text-center mt-2">
            <span className="text-xs text-gray-500">Impact</span>
          </div>
        </div>
      </div>

      {/* Category legend */}
      <div className="mt-3 flex flex-wrap gap-3">
        {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
          <div key={cat} className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-xs text-gray-500">{cat.replace('_', ' ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RiskMatrix;
